/**
 * OAuth 2.0 Authorization Code + PKCE client for the React SPA.
 *
 * Exports consumed by AuthProvider:
 *   loginWithCredentials(email, password)
 *   trySsoLogin()
 *   refreshTokens()
 *   logout()
 *   exchangeCodeForTokens(code)
 */

import { getSpaOAuthConfig } from './oauthConfig';
import { clearAuthenticatedUser } from '../services/applicationInsights';
import config from '../config';
import {
    OAUTH_TOKENS_KEY,
    getStoredOAuthTokens,
    setStoredOAuthTokens,
    clearStoredOAuthTokens
} from './tokenStorage';

// ─── Constants ───────────────────────────────────────────────
const AUTH_CHANNEL = 'oauth-auth';
const BASE_URL = config.API_BASE_URL.replace(/\/+$/, '');
const SKIP_NEXT_SSO_KEY = 'oauth.skip_next_sso';

function shouldLogAuthDebug() {
    return config.ENABLE_LOGGING && config.ENVIRONMENT === 'development' && process.env.NODE_ENV !== 'test';
}

// ─── Module-level state ──────────────────────────────────────
// Load tokens from localStorage on startup so they survive page reloads
function loadStoredTokens() {
    return getStoredOAuthTokens();
}

const authState = {
    tokens: loadStoredTokens(),
    codeVerifier: null,
    channel: null,
    cachedAuth: null  // Cache authorize result from trySsoLogin for reuse
};

// ─── Helpers ─────────────────────────────────────────────────

function randomString(length = 64) {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const bytes = crypto.getRandomValues(new Uint8Array(length));
    return Array.from(bytes, b => charset[b % charset.length]).join('');
}

async function sha256Base64Url(input) {
    const bytes = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function getAuthChannel() {
    if (!authState.channel) {
        if (typeof BroadcastChannel === 'undefined') {
            authState.channel = {
                postMessage: () => {},
                close: () => {},
                onmessage: null
            };
            return authState.channel;
        }

        authState.channel = new BroadcastChannel(AUTH_CHANNEL);
        authState.channel.onmessage = (event) => {
            if (event?.data?.type === 'TOKENS_UPDATED') {
                authState.tokens = getStoredOAuthTokens();
            }
            if (event?.data?.type === 'LOGOUT') {
                authState.tokens = null;
                clearStoredOAuthTokens();
            }
        };
    }
    return authState.channel;
}

function setTokens(tokens) {
    authState.tokens = tokens;
    setStoredOAuthTokens(tokens);
    getAuthChannel().postMessage({ type: 'TOKENS_UPDATED' });
}

function clearTokens() {
    authState.tokens = null;
    clearStoredOAuthTokens();
    try {
        localStorage.removeItem('selected_organization_id');
        localStorage.removeItem('selected_site_collection_id');
    } catch (e) {
        // ignore
    }

    clearAuthenticatedUser();
}

function parseCodeFromRedirectUri(redirectUri) {
    const url = new URL(redirectUri, window.location.origin);
    return url.searchParams.get('code');
}

async function extractOAuthErrorMessage(response, fallbackMessage) {
    try {
        const contentType = response.headers?.get?.('content-type') || '';
        if (contentType.includes('application/json')) {
            const payload = await response.json();
            return payload?.error_description || payload?.message || payload?.error || fallbackMessage;
        }

        const text = await response.text();
        return text || fallbackMessage;
    } catch {
        return fallbackMessage;
    }
}

function markSkipNextSso() {
    sessionStorage.setItem(SKIP_NEXT_SSO_KEY, '1');
}

function consumeSkipNextSso() {
    const shouldSkip = sessionStorage.getItem(SKIP_NEXT_SSO_KEY) === '1';
    if (shouldSkip) {
        sessionStorage.removeItem(SKIP_NEXT_SSO_KEY);
    }
    return shouldSkip;
}

// ─── Core OAuth operations ───────────────────────────────────

/**
 * Step 1 — Call GET {API_BASE_URL}/oauth/authorize with PKCE parameters.
 * Returns either { login_challenge, ... } or { sso: true, redirect_uri }.
 */
async function authorize({ state, nonce }) {
    const cfg = getSpaOAuthConfig();
    authState.codeVerifier = randomString(64);
    const codeChallenge = await sha256Base64Url(authState.codeVerifier);

    const query = new URLSearchParams({
        response_type: 'code',
        client_id: cfg.clientId,
        redirect_uri: cfg.redirectUri,
        scope: cfg.scope,
        state,
        nonce,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256'
    });

    const res = await fetch(`${BASE_URL}/oauth/authorize?${query.toString()}`, {
        method: 'GET',
        credentials: 'include'
    });
    if (!res.ok) {
        throw new Error(await extractOAuthErrorMessage(res, 'Authorize failed'));
    }
    return await res.json();
}

/**
 * Exchange an authorization code for tokens (used by both login and callback).
 */
export async function exchangeCodeForTokens(code) {
    const cfg = getSpaOAuthConfig();
    const body = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        client_id: cfg.clientId,
        redirect_uri: cfg.redirectUri,
        code_verifier: authState.codeVerifier
    });

    const res = await fetch(`${BASE_URL}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body
    });
    if (!res.ok) {
        throw new Error(await extractOAuthErrorMessage(res, 'Token exchange failed'));
    }
    const tokens = await res.json();
    setTokens(tokens);
    return tokens;
}

// ─── Public API ──────────────────────────────────────────────

/**
 * Full credentials login:
 *   authorize → {API_BASE_URL}/oauth/login → token exchange
 */
export async function loginWithCredentials(email, password) {
    // Reuse authorize result cached by trySsoLogin() if available.
    // This avoids a second /oauth/authorize call and keeps the
    // PKCE codeVerifier consistent with the code_challenge sent earlier.
    let state, authPayload;
    if (authState.cachedAuth) {
        ({ payload: authPayload, state } = authState.cachedAuth);
        authState.cachedAuth = null; // consume it — one-time use
    } else {
        // Fallback: no cached auth (e.g. trySsoLogin didn't run yet)
        state = randomString(32);
        const nonce = randomString(32);
        authPayload = await authorize({ state, nonce });
    }

    // If SSO session already exists, skip credential login
    if (authPayload.sso && authPayload.redirect_uri) {
        const code = parseCodeFromRedirectUri(authPayload.redirect_uri);
        return await exchangeCodeForTokens(code);
    }

    const loginRes = await fetch(`${BASE_URL}/oauth/login?state=${encodeURIComponent(state)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
            loginChallenge: authPayload.login_challenge,
            email,
            password
        })
    });

    if (!loginRes.ok) {
        // Try to extract a meaningful error from the response body
        let errorMsg = 'Login failed';
        try {
            const errBody = await loginRes.json();
            errorMsg = errBody.error_description || errBody.message || errBody.error || errorMsg;
        } catch { /* use default */ }
        throw new Error(errorMsg);
    }

    const loginPayload = await loginRes.json();
    const code = parseCodeFromRedirectUri(loginPayload.redirect_uri);
    return await exchangeCodeForTokens(code);
}

/**
 * Silent SSO check on startup:
 *   authorize → if sso=true, exchange code; else return null.
 *
 * Uses a module-level promise lock so that duplicate calls
 * (e.g. React 18 StrictMode double-mount) reuse the same request.
 */
let _ssoPromise = null;

export function trySsoLogin() {
    if (consumeSkipNextSso()) {
        return Promise.resolve(null);
    }

    if (_ssoPromise) return _ssoPromise;

    _ssoPromise = (async () => {
        try {
            const state = randomString(32);
            const nonce = randomString(32);
            const authPayload = await authorize({ state, nonce });

            if (!authPayload.sso || !authPayload.redirect_uri) {
                // No SSO session — cache the authorize result so that
                // loginWithCredentials() can reuse it instead of calling
                // authorize a second time. The codeVerifier is already
                // stored in authState by the authorize() call above.
                authState.cachedAuth = { payload: authPayload, state, nonce };
                return null;
            }

            const code = parseCodeFromRedirectUri(authPayload.redirect_uri);
            return await exchangeCodeForTokens(code);
        } catch (e) {
            if (shouldLogAuthDebug()) {
                console.warn('[OAuth] SSO check failed (expected on first visit):', e.message);
            }
            return null;
        } finally {
            _ssoPromise = null; // Allow future SSO checks (e.g. after logout + re-login)
        }
    })();

    return _ssoPromise;
}

/**
 * Refresh tokens using the current refresh_token.
 */
export async function refreshTokens() {
    const currentRefreshToken = authState.tokens?.refresh_token;
    if (!currentRefreshToken) throw new Error('No refresh token');

    const body = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: currentRefreshToken,
        client_id: getSpaOAuthConfig().clientId
    });

    const res = await fetch(`${BASE_URL}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body
    });
    if (!res.ok) {
        throw new Error(await extractOAuthErrorMessage(res, 'Refresh failed'));
    }
    const refreshed = await res.json();
    setTokens(refreshed);
    return refreshed;
}

/**
 * Full logout: revoke refresh token, clear server session, wipe local state.
 */
export async function logout() {
    authState.cachedAuth = null; // Clear stale authorize cache
    const currentRefreshToken = authState.tokens?.refresh_token;
    const clientId = getSpaOAuthConfig().clientId;

    // Avoid one immediate silent SSO after redirect to login page.
    markSkipNextSso();

    // Revoke refresh token
    if (currentRefreshToken) {
        try {
            await fetch(`${BASE_URL}/oauth/revoke`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    token: currentRefreshToken,
                    token_type_hint: 'refresh_token',
                    client_id: clientId
                })
            });
        } catch (e) {
            if (shouldLogAuthDebug()) {
                console.warn('[OAuth] Revoke failed:', e.message);
            }
        }
    }

    // Clear SSO server-side session cookie
    try {
        const response = await fetch(`${BASE_URL}/oauth/logout`, {
            method: 'POST',
            credentials: 'include',
            keepalive: true
        });

        if (!response.ok) {
            if (shouldLogAuthDebug()) {
                console.warn(`[OAuth] Server logout returned ${response.status}`);
            }
        }
    } catch (e) {
        if (shouldLogAuthDebug()) {
            console.warn('[OAuth] Server logout failed:', e.message);
        }
    }

    // Clear local state after server-side session clear completes.
    clearTokens();
    getAuthChannel().postMessage({ type: 'LOGOUT' });

    // Redirect to login page — full reload ensures clean state
    window.location.href = '/';
}

// ─── Cross-tab sync ──────────────────────────────────────────
window.addEventListener('storage', (event) => {
    if (event.key === OAUTH_TOKENS_KEY) {
        authState.tokens = getStoredOAuthTokens();
    }
});

// Initialize broadcast channel
getAuthChannel();
